import { keepPreviousData, queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, unwrap, type Schemas } from '@/lib/api/client';
import { reasonOf } from '@/lib/api/errors';
import { candidateKeys } from '@/features/candidates/keys';
import { eventKeys } from '@/features/events/api';
import { PAGE_SIZE, type DuplicatesSearch } from './search';
import { isPending } from './vocab';

export type DuplicateGroup = Schemas['DedupClusterDetailDto'];
export type DuplicateGroupItem = Schemas['DedupClusterListItemDto'];
export type GroupMember = Schemas['DedupClusterMemberDto'];

export const duplicateKeys = {
  all: ['duplicates'] as const,
  lists: () => [...duplicateKeys.all, 'list'] as const,
  list: (search: DuplicatesSearch) => [...duplicateKeys.lists(), search] as const,
  detail: (id: string) => [...duplicateKeys.all, 'detail', id] as const,
};

/** Open groups, oldest first: the queue. */
export const openGroupsQuery = (search: DuplicatesSearch) =>
  queryOptions({
    queryKey: duplicateKeys.list(search),
    queryFn: () =>
      unwrap(
        api.GET('/api/scraping/dedup-clusters', {
          params: { query: { resolved: false, pageNumber: search.page, pageSize: PAGE_SIZE } },
        }),
      ),
    placeholderData: keepPreviousData,
  });

/** The open group that names this event as what its records may duplicate, found through the open groups; null when none does. */
export const groupNamingEventQuery = (eventId: string) =>
  queryOptions({
    queryKey: [...duplicateKeys.all, 'naming', eventId] as const,
    queryFn: async () => {
      for (let page = 1; ; page++) {
        const read = await unwrap(
          api.GET('/api/scraping/dedup-clusters', {
            params: { query: { resolved: false, pageNumber: page, pageSize: PAGE_SIZE } },
          }),
        );
        const found = read.items.find((g) => g.suspectedEvent?.raceEventId === eventId);
        if (found) return found;
        if (page * PAGE_SIZE >= read.totalCount || read.items.length === 0) return null;
      }
    },
  });

export const groupQuery = (id: string) =>
  queryOptions({
    queryKey: duplicateKeys.detail(id),
    queryFn: () =>
      unwrap(api.GET('/api/scraping/dedup-clusters/{id}', { params: { path: { id } } })),
  });

export type Decision =
  | { resolution: 'KeepStaged'; winner: string }
  | { resolution: 'KeepExistingEvent' }
  | { resolution: 'NotDuplicates' };

/** What a decision acts on: the pending records and the published event. */
export const groupFingerprint = (group: DuplicateGroupItem): string =>
  [
    group.suspectedEvent?.raceEventId ?? '',
    ...group.members
      .filter(isPending)
      .map((m) => m.stagedEvent.id)
      .sort(),
  ].join(',');

/** The group gained or lost a record after the confirmation named them. */
export class GroupChangedError extends Error {
  constructor() {
    super('The group changed since you opened it.');
    this.name = 'GroupChangedError';
  }
}

/** The resolution, and the approval that follows it: the entry it recorded, or why it was refused. */
export interface Resolved {
  recorded: Schemas['ResolutionRecordedResponse'];
  approval?: { auditEntryId: string | null } | { refusal: string };
}

/**
 * The API rejects whatever is pending when it lands, so a changed group is refused first. With a
 * winner to publish, its approval follows the resolution; a refused approval leaves the group
 * resolved and is returned, not thrown.
 */
export function useResolveGroup(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      decision,
      expected,
      publishStagedEventId,
    }: {
      decision: Decision;
      expected: string;
      publishStagedEventId?: string;
    }): Promise<Resolved> => {
      const fresh = await queryClient.fetchQuery({ ...groupQuery(id), staleTime: 0 });
      if (groupFingerprint(fresh) !== expected) throw new GroupChangedError();
      const recorded = await unwrap(
        api.POST('/api/scraping/dedup-clusters/{id}/resolve', {
          params: { path: { id } },
          body: {
            resolution: decision.resolution,
            winningStagedEventId: decision.resolution === 'KeepStaged' ? decision.winner : null,
          },
        }),
      );
      if (!publishStagedEventId) return { recorded };
      try {
        const approved = await unwrap(
          api.POST('/api/scraping/staged-events/{id}/approve', {
            params: { path: { id: publishStagedEventId } },
          }),
        );
        return { recorded, approval: { auditEntryId: approved.auditEntryId ?? null } };
      } catch (error) {
        return { recorded, approval: { refusal: reasonOf(error) ?? 'The server gave no reason.' } };
      }
    },
    onSettled: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: duplicateKeys.all }),
        queryClient.invalidateQueries({ queryKey: candidateKeys.all }),
        queryClient.invalidateQueries({ queryKey: eventKeys.all }),
      ]),
  });
}
